/**
 * CRUD Operations Hook
 * 
 * Custom hook that provides common CRUD functionality for management modules.
 * Handles data fetching, form state, loading states, and error handling.
 * 
 * @module useCRUDOperations
 */

import { useState, useCallback } from 'react';
import { message } from 'antd';

/**
 * Custom hook for CRUD operations
 * 
 * @param {Object} service - Service object with CRUD methods
 * @param {string} module - Module name for error messages
 * @returns {Object} CRUD utilities and state
 * 
 * @example
 * const {
 *   data,
 *   loading,
 *   renderType,
 *   selectedItem,
 *   fetchData,
 *   handleCreate,
 *   handleUpdate,
 *   handleEdit,
 *   handleAdd,
 *   handleCancel
 * } = useCRUDOperations(FacilityService, 'facility');
 */
export const useCRUDOperations = (service, module) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [renderType, setRenderType] = useState('list');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [initialValues, setInitialValues] = useState(null);

  /**
   * Fetches data using the service with optional filters
   * 
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Array>} Fetched data
   */
  const fetchData = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const result = await service.getAll(filters);
      setData(result);
      return result;
    } catch (error) {
      console.error(`Error fetching ${module} data:`, error);
      message.error(error.message || `Failed to fetch ${module} data`);
      return [];
    } finally {
      setLoading(false);
    }
  }, [service, module]);

  /**
   * Creates a new item
   * 
   * @param {Object} itemData - Data for the new item
   * @returns {Promise<Object>} Creation result
   */
  const handleCreate = useCallback(async (itemData) => {
    setSubmitting(true);
    const loadingMessage = message.loading(`Creating ${module}...`, 0);
    
    try {
      const result = await service.create(itemData);
      loadingMessage(); // Hide loading message
      
      if (result.success) {
        message.success(result.message);
        setRenderType('list');
        return { success: true, data: result.data };
      } else {
        message.error(result.message);
        return { success: false, error: result.message };
      }
    } catch (error) {
      loadingMessage(); // Hide loading message
      console.error(`Error creating ${module}:`, error);
      message.error(error.message || `Failed to create ${module}`);
      return { success: false, error: error.message };
    } finally {
      setSubmitting(false);
    }
  }, [service, module]);

  /**
   * Updates an existing item
   * 
   * @param {string} itemId - ID of the item to update
   * @param {Object} itemData - Updated data
   * @returns {Promise<Object>} Update result
   */
  const handleUpdate = useCallback(async (itemId, itemData) => {
    setSubmitting(true);
    const loadingMessage = message.loading(`Updating ${module}...`, 0);
    
    try {
      const result = await service.update(itemId, itemData);
      loadingMessage(); // Hide loading message
      
      if (result.success) {
        message.success(result.message);
        setRenderType('list');
        return { success: true, data: result.data };
      } else {
        message.error(result.message);
        return { success: false, error: result.message };
      }
    } catch (error) {
      loadingMessage(); // Hide loading message
      console.error(`Error updating ${module}:`, error);
      message.error(error.message || `Failed to update ${module}`);
      return { success: false, error: error.message };
    } finally {
      setSubmitting(false);
    }
  }, [service, module]);

  /**
   * Handles form submission (create or update based on selectedItemId)
   * 
   * @param {Object} formData - Form data to submit
   * @param {Function} onSuccess - Callback to call on success
   * @returns {Promise<Object>} Submission result
   */
  const handleSubmit = useCallback(async (formData, onSuccess) => {
    let result;
    
    if (selectedItemId) {
      result = await handleUpdate(selectedItemId, formData);
    } else {
      result = await handleCreate(formData);
    }
    
    if (result.success && onSuccess) {
      await onSuccess();
    }
    
    return result;
  }, [selectedItemId, handleCreate, handleUpdate]);

  /**
   * Prepares for editing an item
   * 
   * @param {Object} item - Item to edit
   * @param {Function} mapToFormValues - Function to map item to form values
   */
  const handleEdit = useCallback((item, mapToFormValues) => {
    if (mapToFormValues) {
      setInitialValues(mapToFormValues(item));
    } else {
      setInitialValues(item);
    }
    setSelectedItem(item);
    setSelectedItemId(item._id || item.id);
    setRenderType('form');
  }, []);

  /**
   * Prepares for adding a new item
   */
  const handleAdd = useCallback(() => {
    setInitialValues(null);
    setSelectedItem(null);
    setSelectedItemId(null);
    setRenderType('form');
  }, []);

  /**
   * Cancels form operation and returns to list
   */
  const handleCancel = useCallback(() => {
    setInitialValues(null);
    setSelectedItem(null);
    setSelectedItemId(null);
    setRenderType('list');
  }, []);

  /**
   * Gets the current operation type
   * 
   * @returns {string} 'create' or 'update'
   */
  const getOperationType = useCallback(() => {
    return selectedItemId ? 'update' : 'create';
  }, [selectedItemId]);

  /**
   * Refreshes data with current filters
   * 
   * @param {Object} currentFilters - Current filter state
   * @returns {Promise<Array>} Refreshed data
   */
  const refreshData = useCallback(async (currentFilters = {}) => {
    console.log(`Refreshing ${module} data with filters:`, currentFilters);
    return await fetchData(currentFilters);
  }, [fetchData, module]);

  return {
    // Data state
    data,
    loading,
    submitting,
    
    // Form state
    renderType,
    selectedItem,
    selectedItemId,
    initialValues,
    
    // Actions
    fetchData,
    refreshData,
    handleCreate,
    handleUpdate,
    handleSubmit,
    handleEdit,
    handleAdd,
    handleCancel,
    
    // Utilities
    getOperationType,
    setRenderType,
    setData
  };
};
